import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { type ReactNode } from 'react';

import {
  type PartnerOffer,
  type PartnerOfferAction,
} from '@/api/routes/partners/partners.offers.api';
import OfferActionIcon from '@/domain/offers/components/OfferActionIcon';
import OfferCouponCode from '@/domain/offers/components/OfferCouponCode';
import {
  formatOfferDay,
  formatOfferLocationAddress,
  formatOfferPeriod,
  getOfferActionLabel,
  getOfferActionTarget,
  getOfferWhatsappDefaultMessage,
  offerActionKindLabel,
} from '@/domain/offers/utils';
import { useTranslate } from '@/state/i18n';
import { radiusTokens } from '@/theme/themePrimitives';

interface Props {
  offer: PartnerOffer;
}

/** What the user gets, the buttons in order, where it is valid, the requests and the dates. */
const OfferDetailInfo = ({ offer }: Props) => {
  const t = useTranslate('domain.offers.components.OfferDetailInfo');

  const actionDetail = (action: PartnerOfferAction) => {
    if (action.kind === 'lead') return t('leadTarget');
    if (action.kind === 'directions') return t('directionsTarget');
    return getOfferActionTarget(action);
  };

  const rows: [string, ReactNode][] = [
    [t('benefit'), offer.benefit ? <OfferCouponCode code={offer.benefit.code} /> : t('noCoupon')],
    ...(offer.benefit?.terms
      ? ([[t('couponTerms'), offer.benefit.terms]] as [string, ReactNode][])
      : []),
    [
      t('actions'),
      offer.actions.length ? (
        <Stack gap={1.25}>
          {offer.actions.map((action, index) => (
            <Stack key={`${action.kind}-${index}`} direction="row" gap={1} alignItems="flex-start">
              <OfferActionIcon kind={action.kind} fontSize="small" sx={{ mt: 0.25 }} />
              <Stack gap={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600}>
                  {`${index + 1}. ${getOfferActionLabel(action)}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={sx.wrap}>
                  {[offerActionKindLabel[action.kind], actionDetail(action)]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                {action.kind === 'whatsapp' ? (
                  <Typography variant="caption" color="text.secondary" sx={sx.wrap}>
                    {t('message', {
                      message: action.message || getOfferWhatsappDefaultMessage(offer.title),
                    })}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          ))}
        </Stack>
      ) : (
        t('noActions')
      ),
    ],
    [
      t('locations'),
      offer.locations.length ? (
        <Stack gap={1}>
          {offer.locations.map((location) => (
            <Stack key={location.id} gap={0.25}>
              {location.label ? (
                <Typography variant="body2" fontWeight={600}>
                  {location.label}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={sx.wrap}>
                {formatOfferLocationAddress(location)}
                {location.complement ? ` (${location.complement})` : ''}
              </Typography>
            </Stack>
          ))}
        </Stack>
      ) : (
        t('noLocations')
      ),
    ],
    [t('audience'), offer.desiredAudienceText ?? t('noPreference')],
    [
      t('period'),
      formatOfferPeriod(offer.desiredStartsAt, offer.desiredEndsAt) ?? t('noPreference'),
    ],
    [t('submittedAt'), offer.submittedAt ? formatOfferDay(offer.submittedAt) : null],
    [t('reviewedAt'), offer.reviewedAt ? formatOfferDay(offer.reviewedAt) : null],
  ];

  return (
    <Paper variant="outlined" sx={sx.paper}>
      <Stack divider={<Divider />}>
        {rows
          .filter(([, value]) => value)
          .map(([label, value]) => (
            <Stack key={label} gap={0.5} sx={{ py: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              {typeof value === 'string' ? (
                <Typography variant="body2" sx={sx.wrap}>
                  {value}
                </Typography>
              ) : (
                value
              )}
            </Stack>
          ))}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {t('audienceNote')}
      </Typography>
    </Paper>
  );
};

export default OfferDetailInfo;

const sx = {
  paper: {
    borderRadius: radiusTokens.xxl,
    px: 2.5,
    py: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  wrap: { overflowWrap: 'anywhere' },
};
